/*
  Results Display Component
  -------------------------
  Displays ROI calculation results including efficiency score,
  revenue per employee, waste analysis, and agent impact.
*/

import React from 'react';
import EfficiencyGauge from '../EfficiencyGauge';
import RPEGraph from '../RPEGraph';

/**
 * Results display component
 * @param {Object} props
 * @param {Object} props.savings - Calculated savings data
 * @param {Object} props.formData - Form data with industry, employees, revenue, etc.
 * @param {string} props.locale - Current locale
 * @param {Function} props.formatCurrency - Currency formatting function
 * @param {Function} props.t - Translation function
 * @param {Function} props.onGetReport - Handler for "Get Detailed Report" button
 */
const ResultsDisplay = ({ 
  savings, 
  formData, 
  locale, 
  formatCurrency, 
  t, 
  onGetReport 
}) => {
  if (!savings) return null;

  const efficiencyScore = Math.round(
    (formData.processMaturity * 0.3 + formData.automationMaturity * 0.7) * 10
  );

  const getEfficiencyMessage = (score) => {
    if (score < 40) return t('efficiencyScore_low');
    if (score < 80) return t('efficiencyScore_medium');
    return t('efficiencyScore_high');
  };

  return (
    <div className="space-y-6">
      {/* Efficiency Score Section */}
      <div className="mt-8 p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <div className="flex flex-col md:flex-row items-center">
          <div className="w-full md:w-1/2">
            <EfficiencyGauge
              score={efficiencyScore}
              label={t('yourEfficiencyScoreIs')}
              size={300}
              strokeWidth={15}
              t={t}
            />
          </div>
          <div className="w-full md:w-1/2 text-center md:text-left md:pl-6 mt-4 md:mt-0">
            <h3 className="text-2xl font-bold text-gray-900">
              {t('yourEfficiencyScoreIs')} {efficiencyScore}
            </h3>
            <p className="text-gray-600 mt-2">
              {getEfficiencyMessage(efficiencyScore)}
            </p>
            <div className="mt-4 flex justify-center md:justify-start space-x-3">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm">
                {t('letsTalk')}
              </button>
              <button className="border border-gray-300 text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-md text-sm">
                {t('share')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Revenue Per Employee Section */}
      <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <h3 className="text-2xl font-bold text-gray-900">
          {t('revenuePerEmployee')}
          {formData.employeeCount > 0 && (
            <span className="text-blue-600">
              {' '}{formatCurrency(formData.annualRevenue / formData.employeeCount)}
            </span>
          )}
        </h3>
        {formData.employeeCount > 0 && formData.industry && (
          <div className="mt-4">
            <RPEGraph 
              currentRPE={formData.annualRevenue / formData.employeeCount}
              industryId={formData.industry}
              locale={locale}
              currency={formData.currency}
              formatCurrency={formatCurrency}
              employeeCount={formData.employeeCount}
              annualRevenue={formData.annualRevenue}
              t={t}
            />
          </div>
        )}
      </div>
      
      {/* Waste Analysis Section */}
      <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('yourAnnualWasteAnalysis')}
        </h3>
        
        <div className="space-y-4">
          <div className="brand-bg-50 border-l-4 brand-border-400 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 brand-text" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h2a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm brand-text">
                  {t('basedOnYourIndustry')}
                </p>
              </div>
            </div>
          </div>

          <div className="brand-bg-100 p-4 rounded-md border brand-border-200">
            <p className="text-sm font-medium text-gray-700 mb-2">
              {t('aiAgentsAverageSavings')} {t('industry_' + formData.industry)}
            </p>
            <p className="text-2xl font-bold brand-text">
              {(savings.aiAgentsAverage.percentLow * 100).toFixed(0)}% - {(savings.aiAgentsAverage.percentHigh * 100).toFixed(0)}% {t('ofRevenue')}
            </p>
            <p className="text-lg font-medium brand-text mt-1">
              {formatCurrency(savings.aiAgentsAverage.amountLow)} - {formatCurrency(savings.aiAgentsAverage.amountHigh)} {t('annually')}
            </p>
          </div>
        </div>
      </div>

      {/* Potential Savings Section */}
      <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <h4 className="text-md font-medium text-gray-900 mb-4">
          {t('potentialAnnualSavingsWithAutomation')}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-sm text-gray-500">{t('estimatedAnnualSavings')}</p>
            <p className="text-2xl font-bold brand-text">
              {formatCurrency(formData.annualRevenue * (savings.industryWaste.percent * savings.adminWaste.low))}
            </p>
          </div>
          
          <div>
            <p className="text-sm text-gray-500">{t('annualHoursEquivalent')}</p>
            <p className="text-2xl font-bold brand-text">
              {new Intl.NumberFormat(locale).format(Math.round(savings.fteSavings.low * 2080))} {t('hours')}
            </p>
          </div>
        </div>

        {/* Selected Agents Impact */}
        {savings.agentImpact && savings.agentImpact.total > 0 && (
          <div className="mt-6 border-t pt-6">
            <h5 className="text-sm font-semibold text-gray-900 mb-3">
              {t('impactFromSelectedAgents')}
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-3 rounded border">
                <p className="text-xs text-gray-900">{t('laborSavings')}</p>
                <p className="font-semibold text-gray-900">
                  {formatCurrency(savings.agentImpact.laborSavings)}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded border">
                <p className="text-xs text-gray-900">{t('revenueImpact')}</p>
                <p className="font-semibold text-gray-900">
                  {formatCurrency(savings.agentImpact.revenueImpact)}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded border">
                <p className="text-xs text-gray-900">{t('employeeSatisfactionImprovement')}</p>
                <p className="font-semibold text-gray-900">
                  {Math.round((savings.agentImpact.employeeSatisfactionImprovement || 0) * 100)}%
                </p>
              </div>
            </div>
            <div className="mt-3 bg-green-50 p-3 rounded border border-green-200">
              <p className="text-base font-semibold text-green-700">
                {t('totalAgentDrivenAnnualBenefit')}
              </p>
              <p className="text-3xl font-bold text-green-700">
                {formatCurrency(savings.agentImpact.total)}
              </p>
            </div>
          </div>
        )}

        {/* Get Report Button */}
        <div className="mt-6">
          <button
            onClick={onGetReport}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm font-medium"
          >
            {t('getDetailedReport')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;
